import React, { useEffect, useRef, useState } from 'react';
import { clone, set } from 'lodash';
import classNames from 'classnames';
import {
  TranslationRules,
  TranslationRulesId,
  TranslationKey,
  Translations,
  TranslationItem,
  TranslationOverrideType,
  TranslationRulesClient,
} from '@upstart/harmony/lib/legos/translationRulesTypes';
import {
  AllTypeaheadOwnAndInjectedProps,
  Menu,
  MenuItem,
  Typeahead,
  TypeaheadMenuProps,
  TypeaheadResult,
  TypeaheadState,
} from 'react-bootstrap-typeahead';
import {
  BootstrapTable,
  Options,
  TableHeaderColumn,
} from 'react-bootstrap-table';
import moment from 'moment';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { Alert, Button, ButtonGroup, Modal } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router-dom';
import SidebarContainer from '../SidebarContainer';
import TranslationRulesService from '../../services/TranslationRulesService';
import Input from '../../components/GlobalUI/ProdigyInput';
import Select from '../../components/GlobalUI/ProdigySelect';
import ConfirmModal from '../../components/ConfirmModal';
import './TranslationRulesEditor.scss';

const SUPPORTED_LANGUAGES = [
  {
    langKey: 'en-US',
    langLabel: 'English',
  },
  {
    langKey: 'es-MX',
    langLabel: 'Spanish',
  },
];

const TABLE_OPTIONS: Options = {
  noDataText: 'There are no overriden translations yet.',
};

const TABLE_STYLES = {
  border: 'none',
  padding: '10px 0 40px',
};

const MAX_RESULTS = 20;

interface MatchParams {
  translationRuleId?: string;
}

const transformToList = (translations: Translations) => {
  const textItems: Record<TranslationKey, TranslationItem> = {};
  Object.entries(translations).forEach(([langKey, langTranslations]) => {
    Object.entries(langTranslations.translation ?? {}).forEach(
      ([key, text]) => {
        const textItem = textItems[key] || { key, translations: {} };
        textItem.translations[langKey] = text;
        textItems[key] = textItem;
      },
    );
  });

  return Object.entries(textItems)
    .sort((i1, i2) => i1[0].localeCompare(i2[0]))
    .map(([, translationListItem]) => translationListItem);
};

const checkOptionMatches = (
  option: TranslationItem,
  menuProps: AllTypeaheadOwnAndInjectedProps<TranslationItem>,
) => {
  const searchableTexts = [
    option.key,
    ...Object.values(option.translations),
  ].filter(text => !!text);
  return searchableTexts.some(text =>
    text.toLowerCase().includes(menuProps.text?.toLowerCase() ?? ''),
  );
};

const renderMenu = (
  options: TypeaheadResult<TranslationItem>[],
  menuProps: TypeaheadMenuProps<TranslationItem>,
  state: TypeaheadState<TranslationItem>,
) => (
  <Menu {...menuProps}>
    {options.map((option, index) => (
      <MenuItem
        className={classNames(
          {
            active: state.activeIndex === index,
          },
          'translationTypeaheadItem',
        )}
        option={option}
        position={index}
        key={option.key}
      >
        <div>
          <b>Translation Key:</b> &quot;{option.key}&quot;
        </div>
        <div>
          {Object.entries(option.translations).map(([lang, text]) => (
            <div key={lang} className="translationTypeaheadItemLine">
              <b>&quot;{lang}&quot; text:</b> &quot;{text}&quot;
            </div>
          ))}
        </div>
      </MenuItem>
    ))}
  </Menu>
);

const getLabelKey = (option: TranslationItem) =>
  `Translation Key: ${option.key}`;

const fetchTranslationRules = (
  translationRuleId: TranslationRulesId | undefined,
) => {
  if (translationRuleId) {
    return TranslationRulesService.get(translationRuleId);
  }
  return Promise.resolve(null);
};

const fetchTranslationRulesFile = (
  translationRules: TranslationRules | undefined,
): Promise<Translations> => {
  const fileURL =
    translationRules?.rulesPreviewFileURL || translationRules?.rulesFileURL;
  // Fetch existing translation overrides
  if (fileURL) {
    return fetch(fileURL).then(response => response.json());
  }
  return Promise.resolve({});
};

function TranslationRulesEditor(props: RouteComponentProps<MatchParams>) {
  // Base properties for translation rules object
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [modal, setModal] = useState({ show: false, text: '' });
  const formRef = useRef<HTMLFormElement>(null);
  const [client, setClient] = useState<TranslationRulesClient>(
    TranslationRulesClient.WEB,
  );

  // Store translation items for typeahead
  const [typeaheadItems, setTypeaheadItems] = useState<TranslationItem[]>([]);
  // Store items that were already overriden
  const [overridenItems, setOverridenItems] = useState<TranslationItem[]>([]);
  // Fetch translation rules object to edit
  const readTranslationRules = useAsync<TranslationRules>(
    fetchTranslationRules,
    [props.match.params.translationRuleId],
  );
  // Call create to save a new translation rules object
  const createTranslationRules = useAsyncCallback<TranslationRules>(
    TranslationRulesService.create,
  );
  // Call update to update an existing translation rules object
  const updateTranslationRules = useAsyncCallback<TranslationRules>(
    TranslationRulesService.update,
  );
  // Fetch default translations to show typeahead options
  const readDefaultTranslations = useAsync<Translations>(
    TranslationRulesService.getDefault,
    [client],
  );
  // Fetch translation file to show translations that were overriden before
  const readTranslationRulesFile = useAsync<Translations>(
    fetchTranslationRulesFile,
    [readTranslationRules.result],
  );

  const openListPage = () => {
    props.history.push('/translationRules');
  };

  useEffect(() => {
    if (readTranslationRules.result) {
      // Set base properties
      setId(readTranslationRules.result.id);
      setName(readTranslationRules.result.name ?? '');
      setClient(
        readTranslationRules.result.client ?? TranslationRulesClient.WEB,
      );
    }
  }, [readTranslationRules.result]);

  useEffect(() => {
    if (readDefaultTranslations.result) {
      setTypeaheadItems(transformToList(readDefaultTranslations.result));
    }
  }, [readDefaultTranslations.result]);

  useEffect(() => {
    if (readTranslationRulesFile.result) {
      setOverridenItems(transformToList(readTranslationRulesFile.result));
    }
  }, [readTranslationRulesFile.result]);

  const isLoading =
    readTranslationRules.loading ||
    createTranslationRules.loading ||
    updateTranslationRules.loading;
  const isCreatePage = !readTranslationRules.result?.id;

  const TranslationTextCell = React.useCallback(
    (cell, translationItem, extra, rowIndex) => (
      <div>
        {SUPPORTED_LANGUAGES.map(({ langKey, langLabel }) => {
          const value = translationItem.translations?.[langKey] ?? '';
          return (
            <div key={langKey} className="translationItemLangRow">
              <div className="translationItemLang">
                {langLabel} ({langKey}):{' '}
              </div>
              <textarea
                value={value}
                className="translationItemLangText"
                onChange={e => {
                  const updatedItems = clone(overridenItems);
                  const prevItem = updatedItems[rowIndex];
                  updatedItems[rowIndex] = {
                    ...prevItem,
                    translations: {
                      ...prevItem.translations,
                      [langKey]: e.target.value,
                    },
                  };
                  setOverridenItems(updatedItems);
                }}
              />
            </div>
          );
        })}
      </div>
    ),
    [overridenItems, setOverridenItems],
  );

  const DeleteCell = React.useCallback(
    (cell, translationItem) => (
      <Button
        bsStyle="danger"
        onClick={() => {
          setOverridenItems(
            overridenItems.filter(item => item.key !== translationItem.key),
          );
        }}
      >
        Delete
      </Button>
    ),
    [overridenItems, setOverridenItems],
  );

  const handleSubmit = async (
    overrideType: TranslationOverrideType | undefined,
  ) => {
    // Validate form
    if (!formRef.current?.checkValidity()) {
      return;
    }

    if (isCreatePage) {
      // Create new translation rules object with the given params
      createTranslationRules
        .execute({ id, name, client })
        .then(created => {
          // Open editor page for the new translation rules
          props.history.push(`/translationRule/${created.id}`);

          setModal({
            show: true,
            text: 'Successfully created a new translation rules file.',
          });
        })
        .catch(() => {});
    } else {
      // Generate translations json from the list of overriden items
      const newOverrides: Translations = overridenItems.reduce((acc, item) => {
        Object.entries(item.translations).forEach(([langKey, text]) => {
          set(acc, [langKey, 'translation', item.key], text);
        });
        return acc;
      }, {});

      // Update the existing translation rules object
      updateTranslationRules
        .execute({ id, name, client }, newOverrides, overrideType)
        .then(() => {
          readTranslationRules.execute(id);
          if (overrideType === TranslationOverrideType.PREVIEW) {
            setModal({
              show: true,
              text: 'Successfully saved translation rules for preview.',
            });
          } else if (overrideType === TranslationOverrideType.PUBLIC) {
            setModal({
              show: true,
              text: 'Successfully published translation rules.',
            });
          }
        })
        .catch(() => {});
    }
  };

  return (
    <SidebarContainer
      openSubMenu="ClientManagement"
      title={
        isCreatePage
          ? 'Create Translation Rules'
          : `Edit Translation Rules: ${readTranslationRules.result?.name}`
      }
    >
      <div className="dealers-table">
        <div className="dealer-editor-container">
          {isLoading && <Alert>Loading...</Alert>}
          {(readTranslationRules?.error || readDefaultTranslations?.error) && (
            <Alert bsStyle="danger">Failed to fetch translation rules.</Alert>
          )}
          {(createTranslationRules?.error || updateTranslationRules?.error) && (
            <Alert bsStyle="danger">Failed to save translation rules.</Alert>
          )}
          <Modal
            show={modal.show}
            onHide={() => setModal({ show: false, text: '' })}
          >
            <Modal.Body>
              <h4>{modal.text}</h4>
              <div className="flex justify-end">
                <Button onClick={() => setModal({ show: false, text: '' })}>
                  Close
                </Button>
              </div>
            </Modal.Body>
          </Modal>
          <form ref={formRef}>
            <Input
              label="ID"
              value={id}
              required
              disabled={!isCreatePage}
              onChangeHandler={setId}
            />
            <Input
              label="Name"
              value={name}
              required
              onChangeHandler={setName}
            />
            <div className="prodigy-input-container">
              <label className="label">Client</label>
              <Select
                id="client"
                options={[
                  { label: 'Web', value: TranslationRulesClient.WEB },
                  { label: 'Instore', value: TranslationRulesClient.INSTORE },
                ]}
                disabled={!isCreatePage}
                onChange={(option: any) => setClient(option.value)}
                value={client}
                searchable={false}
              />
            </div>
            {!isCreatePage && (
              <>
                {readTranslationRules.result && (
                  <Alert bsStyle="info">
                    Translation rules last published at:{' '}
                    {readTranslationRules.result.publishedAt
                      ? moment(readTranslationRules.result.publishedAt).format(
                          'llll',
                        )
                      : 'N/A'}
                  </Alert>
                )}
                {readTranslationRules.result?.rulesPreviewFileURL && (
                  <Alert bsStyle="warning">
                    There are unpublished translations saved for preview. Please
                    verify them using the website and publish.
                  </Alert>
                )}
                <Typeahead
                  className="translationTypeahead"
                  id="translation-list-typeahead"
                  placeholder="Type to look up translations"
                  isLoading={readDefaultTranslations.loading}
                  maxResults={MAX_RESULTS}
                  selectHintOnEnter
                  selected={[]}
                  paginate={false}
                  options={typeaheadItems}
                  // Custom filtering for translation items
                  filterBy={checkOptionMatches}
                  // Custom label for translation items
                  labelKey={getLabelKey}
                  // Custom rendering for translation item suggestions
                  renderMenu={renderMenu}
                  onChange={([selectedItem]) => {
                    const isOverriden = overridenItems.some(
                      item => item.key === selectedItem.key,
                    );
                    if (!isOverriden) {
                      setOverridenItems([...overridenItems, selectedItem]);
                    }
                  }}
                />
                <BootstrapTable
                  data={overridenItems}
                  options={TABLE_OPTIONS}
                  tableStyle={TABLE_STYLES}
                >
                  <TableHeaderColumn dataField="key" isKey width="250px">
                    Translation Key
                  </TableHeaderColumn>
                  <TableHeaderColumn dataFormat={TranslationTextCell}>
                    Translation Text
                  </TableHeaderColumn>
                  <TableHeaderColumn
                    width="120px"
                    dataField="button"
                    columnClassName="list-action-column"
                    dataFormat={DeleteCell}
                  />
                </BootstrapTable>
              </>
            )}
            <ButtonGroup>
              <Button type="button" onClick={openListPage}>
                Go Back
              </Button>
              {isCreatePage ? (
                <Button
                  type="submit"
                  onClick={() => handleSubmit(undefined)}
                  bsStyle="primary"
                  disabled={isLoading}
                >
                  Create
                </Button>
              ) : (
                <>
                  <Button
                    type="submit"
                    bsStyle="primary"
                    onClick={() =>
                      handleSubmit(TranslationOverrideType.PREVIEW)
                    }
                    disabled={isLoading}
                  >
                    Save for Preview
                  </Button>
                  <Button
                    type="button"
                    bsStyle="primary"
                    onClick={() => {
                      if (formRef.current?.reportValidity()) {
                        ConfirmModal({
                          text: 'Are you sure you want to publish changes?',
                        }).then((confirmed: boolean) => {
                          if (confirmed) {
                            handleSubmit(TranslationOverrideType.PUBLIC);
                          }
                        });
                      }
                    }}
                    disabled={isLoading}
                  >
                    Save and Publish
                  </Button>
                </>
              )}
            </ButtonGroup>
          </form>
        </div>
      </div>
    </SidebarContainer>
  );
}

export default TranslationRulesEditor;
